(() => {
  'use strict';
  const P=window.HouseholdPlanner;
  const root=window.Shopify?.routes?.root||'/';
  const labels=JSON.parse(document.getElementById('garrnish-cart-labels').textContent);
  async function request(path,body) {
    let response, data;
    try {
      response=await fetch(root+path,{credentials:'same-origin',cache:'no-store',headers:body?{'Content-Type':'application/json','Accept':'application/json'}:{'Accept':'application/json'},...(body?{method:'POST',body:JSON.stringify(body)}:{})});
      data=await response.json();
      if(!data||typeof data!=='object')throw new Error('Invalid cart response');
    } catch(error) {
      console.warn('Garrnish cart request failed:',{path,status:response?.status,error:error.name});
      throw new Error(labels.retry);
    }
    if(!response.ok||data.status)throw new Error(typeof data.description==='string'?data.description:labels.retry);
    return data;
  }
  const read=()=>request('cart.js');
  function sectionRequest() {
    const sections=document.querySelector('cart-drawer')?.getSectionsToRender?.().map(s=>s.id)||[];
    return {sections,sections_url:location.pathname};
  }
  // The retired codes (P.DISCOUNTS) no longer exist in the store (checked 14 Oct 2026), so they can't
  // discount anything; they're only removed when one actually shows up on the cart.
  const staleCodes = cart => P.discountCodes(cart).some(code => Object.values(P.DISCOUNTS).includes(code));
  let offerPending = null;
  async function ensureOffer(cart) {
    if (offerPending) { await offerPending; return ensureOffer(await read()); }
    if (cart.currency !== 'SGD') throw new Error(labels.invalid);
    if (!staleCodes(cart)) return cart;
    offerPending = request('cart/update.js', {discount:P.selectedCodes(cart).join(','), ...sectionRequest()});
    try {
      const updated = await offerPending;
      cartVersion++;
      const drawer = document.querySelector('cart-drawer');
      if (drawer && !drawer.isOpen && drawer.cartType !== 'page' && updated.sections && drawer.getSectionsToRender().every(section => updated.sections[section.id])) {
        drawerRevision++;
        drawer.renderContents(updated, false);
      }
      return updated;
    } finally { offerPending = null; }
  }
  // Adding a line never changes the cart's discount codes, so the cart read before the add decides
  // whether retired codes need clearing. Usually they don't: one cart/add.js call returns the added
  // lines and the drawer, and the cart after it is the pre-add cart with those lines merged in
  // (Shopify puts new lines first). The panel re-reads the full cart once the drawer is open, and
  // checkout re-checks it. Otherwise one cart/update.js call clears the codes and returns the full cart.
  async function add(item, before) {
    if (before.currency !== 'SGD') throw new Error(labels.invalid);
    if (!staleCodes(before)) {
      const result = await request('cart/add.js', {items:[item], ...sectionRequest()});
      if (!Array.isArray(result.items)) throw new Error(labels.retry);
      const items = [...result.items, ...before.items.filter(line => !result.items.some(added => added.key === line.key))];
      return {...before, items, item_count: items.reduce((sum, line) => sum + line.quantity, 0), sections: result.sections};
    }
    await request('cart/add.js', {items:[item]});
    const pending = request('cart/update.js', {discount: P.selectedCodes(before).join(','), ...sectionRequest()});
    offerPending = pending;
    try { return await pending; }
    finally { if (offerPending === pending) offerPending = null; }
  }
  let busy = false;
  // A cart read started as soon as an add button is pressed, so it's already under way when the add
  // begins. It's only used while fresh and if nothing in this tab changed the cart since it started.
  let early = null, cartVersion = 0;
  function prefetch() {
    if (busy || (early && early.version === cartVersion && Date.now() - early.at < 3000)) return;
    const promise = read();
    promise.catch(() => {});
    early = {promise, version: cartVersion, at: Date.now()};
  }
  function readFresh() {
    const pending = early; early = null;
    if (pending && pending.version === cartVersion && Date.now() - pending.at < 5000) return pending.promise.catch(() => read());
    return read();
  }

  const subscriptions = cart => cart.items.filter(item => item.selling_plan_allocation);
  function singleSubscription(cart, editing) {
    if (subscriptions(cart).some(item => item.key !== editing?.key)) throw new Error(labels.invalid);
  }
  async function mutate(action) {
    if (busy) throw new Error(labels.retry);
    busy = true;
    document.dispatchEvent(new CustomEvent('garrnish:cart-busy', {detail:true}));
    try {
      const execute = async () => { if (offerPending) await offerPending; return action(); };
      const locks = window.navigator?.locks;
      if (!locks) return await execute();
      // Another tab holding the cart may change it after the early read started: wait, then read afresh.
      return await locks.request('garrnish-cart', {ifAvailable: true}, async lock => {
        if (lock) return execute();
        early = null;
        return locks.request('garrnish-cart', execute);
      });
    } finally {
      busy = false;
      cartVersion++;
      document.dispatchEvent(new CustomEvent('garrnish:cart-busy', {detail:false}));
    }
  }
  async function write(item, editing) {
    return mutate(async () => {
      const current = await readFresh();
      // Lines to remove after the write: a cart holds a subscription or one-time boxes, never both.
      let remove = [];
      if (typeof item === 'function') {
        const selection = item(current);
        if (!selection) return null;
        ({item, editing, remove = []} = selection);
      }
      if (item.selling_plan) {
        singleSubscription(current, editing);
      }
      if (item.selling_plan) {
        let row;
        try { row=P.restore({properties:item.properties}).row; } catch (_) { throw new Error(labels.invalid); }
        if (item.quantity!==row.boxes || Number(item.selling_plan)!==P.planId(row)) throw new Error(labels.invalid);
      }
      let updated;
      if (!editing) {
        updated = await add(item, current);
      } else {
        const index = current.items.findIndex(line => line.key === editing.key && (P.managed(line) || !item.selling_plan));
        if (index < 0) throw new Error(labels.invalid);
        const result = await request('cart/change.js', {line:index+1, quantity:item.quantity, selling_plan:item.selling_plan, properties:{...current.items[index].properties,...item.properties}, ...sectionRequest()});
        updated = await ensureOffer(result);
        updated = {...updated, sections: updated.sections || result.sections};
      }
      // Removed after the add or change succeeds, so a failure never leaves the shopper with nothing.
      remove = remove.filter(key => key !== editing?.key);
      if (remove.length) {
        const result = await request('cart/update.js', {updates:Object.fromEntries(remove.map(key => [key, 0])), ...sectionRequest()});
        updated = await ensureOffer(result);
        updated = {...updated, sections: updated.sections || result.sections};
      }
      if (item.selling_plan) {
        const matched=updated.items.find(line => Number(line.variant_id)===Number(item.id) &&
          Number(line.selling_plan_allocation?.selling_plan?.id)===Number(item.selling_plan) &&
          line.quantity===item.quantity && Object.entries(item.properties).every(([key,value])=>String(line.properties?.[key])===String(value)));
        if (!matched || !P.validLine(matched) || !P.validDiscount(updated)) throw new Error(labels.invalid);
      }
      return updated;
    });
  }
  async function changeQuantity(key, quantity) {
    return mutate(async () => {
      const current = await read();
      const index = current.items.findIndex(item => item.key === key);
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error(labels.invalid);
      // A removed/rekeyed line must refresh, never delete an unrelated line by index.
      if (index < 0 && quantity === 0) return current;
      if (index < 0) throw new Error(labels.retry);
      if (P.managed(current.items[index]) && quantity !== 0) throw new Error(labels.invalid);
      const result = await request('cart/change.js', {line:index+1, quantity, ...sectionRequest()});
      if (quantity === 0) {
        // The removal has already succeeded. Discount cleanup failure must not
        // report it as a failed removal or resurrect the deleted subscription.
        try { return await ensureOffer(result); } catch (_) { return result; }
      }
      return ensureOffer(result);
    });
  }
  async function upgrade(quoted, state) {
    return mutate(async () => {
      const cart = await read();
      const offer = P.cartOffer(cart, state.variants);
      if (P.cartOfferSignature(offer) !== P.cartOfferSignature(quoted) || !offer) {
        await open();
        throw new Error(state.labels.changed);
      }
      const index = cart.items.findIndex(item => item.key === offer.key);
      const original = cart.items[index];
      singleSubscription(cart, original);
      let updated;
      try {
        updated = await request('cart/change.js', {
          line:index+1, quantity:offer.row.boxes, selling_plan:offer.sellingPlan,
          properties:{...original.properties,...P.properties([offer.size],offer.row)}, ...sectionRequest()
        });
        updated = await ensureOffer(updated);
        const actual = updated.items.find(item => P.matchesSelection(item,[offer.size],offer.row,offer.variantId));
        if (!actual || actual.final_line_price !== offer.price.subtotal || !P.validDiscount(updated)) {
          throw new Error(state.labels.unavailable);
        }
      } catch (error) {
        try {
          const current = await read();
          const targets = current.items.filter(item => Number(item.variant_id) === Number(offer.variantId) && Number(item.selling_plan_allocation?.selling_plan?.id) === offer.sellingPlan && item.quantity === offer.row.boxes && item.properties?._garrnish_sizes === offer.size);
          if (targets.length === 1) {
            let restored = await request('cart/change.js', {line:current.items.indexOf(targets[0])+1, quantity:original.quantity, selling_plan:original.selling_plan_allocation?.selling_plan?.id || null, properties:original.properties, ...sectionRequest()});
            restored = await ensureOffer(restored);
            present(restored);
          } else if (!current.items.some(item => item.key === original.key)) {
            await open();
            throw new Error(state.labels.changed);
          }
        } catch (_) {
          throw new Error(state.labels.changed);
        }
        throw new Error(state.labels.unavailable);
      }
      present(updated);
      const status = document.getElementById('CartDrawer-LiveRegionText');
      if (status) { status.textContent=state.labels.success; status.removeAttribute('aria-hidden'); }
      return updated;
    });
  }
  let drawerRevision=0;
  function present(result) {
    drawerRevision++;
    const drawer=document.querySelector('cart-drawer');
    if(drawer&&drawer.cartType!=='page'&&result.sections&&drawer.getSectionsToRender().every(s=>result.sections[s.id])) {
      drawer.setActiveElement?.(document.activeElement);
      if(!drawer.isOpen)drawer.rememberOpener?.();
      drawer.renderContents(result,!drawer.isOpen);
      document.dispatchEvent(new CustomEvent('garrnish:cart-updated'));
    } else location.assign(root+'cart');
  }
  async function open() {
    const drawer=document.querySelector('cart-drawer');
    if(!drawer||drawer.cartType==='page'){location.assign(root+'cart');return;}
    if(!drawer.isOpen) {
      drawer.setActiveElement?.(document.activeElement);
      drawer.rememberOpener?.();
      drawer.toggleDrawerClasses();
    }
    const revision=++drawerRevision;
    const ids=drawer.getSectionsToRender().map(section=>section.id);
    await ensureOffer(await read());
    if(revision!==drawerRevision)return;
    const sections=await request('?sections='+ids.join(','));
    if(revision!==drawerRevision)return;
    if(ids.some(id=>!sections[id]))throw new Error(labels.retry);
    const focused=document.activeElement;
    const focusId=drawer.contains(focused)?focused.id:null;
    drawer.renderContents({sections},false);
    if(drawer.isOpen) {
      drawer.toggleEelements().forEach(element=>element.setAttribute('tabindex','0'));
      if(focusId!==null&&!drawer.contains(document.activeElement)) {
        const target=focusId?document.getElementById(focusId):null;
        (target&&drawer.contains(target)?target:drawer.closeButton()).focus({preventScroll:true});
      }
    }
    document.dispatchEvent(new CustomEvent('garrnish:cart-updated'));
  }

  function showIssue(item,message) {
    for(const old of document.querySelectorAll('[data-household-cart-error]'))old.remove();
    const scopes=document.querySelectorAll('cart-drawer .wt-cart__drawer__footer, #main-cart-footer, cart-drawer-items, cart-items');
    for(const scope of scopes) {
      const note=document.createElement('p');note.className='gh-cart__error';note.dataset.householdCartError='';note.setAttribute('role','alert');
      note.append(document.createTextNode(message+' '));
      if(item){const link=document.createElement('a');link.href=root+'?garrnish_edit='+encodeURIComponent(item.key)+'#offer';link.textContent=labels.change;note.append(link);}
      scope.prepend(note);
    }
  }
  async function checkout() {
    const cart=await ensureOffer(await read());
    if (busy) return;
    const review=cart.items.find(item=>P.managed(item)&&!P.validLine(item));
    if(review){await open();showIssue(review,'Please edit your plan or household to refresh this subscription before checkout.');return;}
    if (!P.validDiscount(cart)) { await open(); showIssue(null, labels.invalid); return; }
    if(subscriptions(cart).length>1){await open();showIssue(null,labels.invalid);return;}
    const invalid=cart.items.find(item=>P.managed(item)&&!P.validLine(item));
    if(invalid){await open();showIssue(invalid,labels.invalid);return;}
    if(!cart.items.length){await open();return;}
    location.assign(root+'checkout');
    return true;
  }
  function checkoutLoading(element) {
    if(!element)return ()=>{};
    const content=[...element.childNodes];
    const busyBefore=element.getAttribute('aria-busy');
    const label=document.createElement('span');label.className='gc-checkout-loading';
    const spinner=document.createElement('span');spinner.className='gc-action-spinner';spinner.setAttribute('aria-hidden','true');
    label.append(spinner,document.createTextNode(labels.loadingCheckout));
    element.replaceChildren(label);element.setAttribute('aria-busy','true');
    return ()=>{element.replaceChildren(...content);if(busyBefore===null)element.removeAttribute('aria-busy');else element.setAttribute('aria-busy',busyBefore);};
  }
  const approved=new WeakSet();let checking=false;
  async function guard(event,form,submitter,href) {
    if(form&&approved.has(form)){approved.delete(form);return;}
    event.preventDefault();event.stopImmediatePropagation();
    if(checking)return;checking=true;
    const restore=checkoutLoading(submitter||event.target.closest('a[href]'));
    let navigating=false;
    try {
      if(busy)return;
      const cart=await ensureOffer(await read());
      const review=cart.items.find(item=>P.managed(item)&&!P.validLine(item));
      if(review){showIssue(review,'Please edit your plan or household to refresh this subscription before checkout.');return;}
      if (!P.validDiscount(cart)) { showIssue(null, labels.invalid); return; }
      if(subscriptions(cart).length>1){showIssue(null,labels.invalid);return;}
      const invalid=cart.items.find(item=>P.managed(item)&&!P.validLine(item));
      if(invalid){showIssue(invalid,labels.invalid);return;}
      navigating=true;
      if(form){approved.add(form);form.requestSubmit(submitter);}else location.assign(href);
    }catch(_){navigating=false;showIssue(null,labels.retry);}finally{if(!navigating){restore();checking=false;}else window.addEventListener('pageshow',()=>{restore();checking=false;},{once:true});}
  }
  document.addEventListener('submit',event=>{
    if(event.submitter?.name==='checkout')guard(event,event.target,event.submitter);
  },true);
  document.addEventListener('click',event=>{
    const link=event.target.closest('a[href]');if(!link)return;
    const url=new URL(link.href,location.origin);
    // A second edit can point to this exact page after a successful cart update.
    // Reload it so the drawer closes and the saved household is restored again.
    if(url.origin===location.origin&&url.searchParams.has('garrnish_edit')&&url.pathname===location.pathname&&url.search===location.search&&event.button===0&&!event.metaKey&&!event.ctrlKey&&!event.shiftKey&&!event.altKey){
      event.preventDefault();location.hash=url.hash;location.reload();return;
    }
    if(url.origin===location.origin&&/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?checkout\/?$/.test(url.pathname))guard(event,null,null,url.href);
  },true);
  window.GarrnishCart={read,prefetch,ensureOffer,write,present,open,checkout,changeQuantity,upgrade,get busy(){return busy;}};
})();
